import { useState } from 'react';
import { Loader2, Plus, Settings2, Trash2, X } from 'lucide-react';
import { apiClient } from '../../../api';
import type { ScopesResponse } from './types';

interface ConditionDraft {
  field: string;
  op: string;
  value: string;
}

const OP_LABEL: Record<string, string> = {
  eq: 'equals',
  ne: 'does not equal',
  in: 'is one of (comma-separated)',
  contains: 'contains',
  starts_with: 'starts with',
  is_true: 'is true',
};

function toCondition(c: ConditionDraft) {
  if (c.op === 'is_true') return { field: c.field, op: c.op };
  if (c.op === 'in') return { field: c.field, op: c.op, value: c.value.split(',').map((v) => v.trim()).filter(Boolean) };
  return { field: c.field, op: c.op, value: c.value.trim() };
}

// Admin-only: add or remove a population basis and choose which basis drives
// the executive live-universe figure. Every change is plain configuration —
// the backend stores it per tenant, so no deploy is involved.
export default function BasisManager({ scopes, basis, onBasisChange, onChanged }: {
  scopes: ScopesResponse;
  basis: string;
  onBasisChange: (basis: string) => void;
  onChanged: () => void;
}) {
  const fields = scopes.basis_fields || [];
  const ops = scopes.basis_ops || [];
  const blankCondition = (): ConditionDraft => ({ field: fields[0] || '', op: 'eq', value: '' });

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [key, setKey] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [conditions, setConditions] = useState<ConditionDraft[]>(() => [blankCondition()]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
      return true;
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Request failed');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addBasis = async () => {
    const built = conditions.map(toCondition);
    const ok = await run(() => apiClient.post('/api/cloud-portfolio/bases', {
      basis_key: key,
      title,
      description: description || null,
      predicate: built.length === 1 ? built[0] : { all: built },
    }));
    if (ok) {
      setKey('');
      setTitle('');
      setDescription('');
      setConditions([blankCondition()]);
    }
  };

  const removeBasis = async (basisKey: string) => {
    if (!window.confirm(`Remove population basis ${basisKey}? Counts under this lens will no longer be available.`)) return;
    // Leave the lens before it disappears so no view refetches a removed basis.
    await run(async () => {
      await apiClient.delete(`/api/cloud-portfolio/bases/${encodeURIComponent(basisKey)}`);
      if (basis === basisKey) onBasisChange(scopes.default_basis);
    });
  };

  const setLive = (liveBasis: string) => run(() => apiClient.patch('/api/cloud-portfolio/bases/live', { live_basis: liveBasis }));

  const updateCondition = (i: number, patch: Partial<ConditionDraft>) => {
    setConditions((list) => list.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  };

  const custom = scopes.bases.filter((b) => b.removable);
  const canSave = !busy && key.trim() && title.trim()
    && conditions.every((c) => c.field && c.op && (c.op === 'is_true' || c.value.trim()));

  if (!open) {
    return (
      <button
        data-testid="cloud-basis-manage-open"
        onClick={() => setOpen(true)}
        className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:underline"
      >
        <Settings2 className="w-3 h-3" /> Manage bases (admin)
      </button>
    );
  }

  return (
    <div data-testid="cloud-basis-manager" className="mt-3 border-t border-gray-100 pt-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-super-dark-purple">Manage bases · admin</span>
        <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600" aria-label="Close basis manager">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {error && <p data-testid="cloud-basis-error" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">{error}</p>}

      <div className="flex items-center gap-2 flex-wrap text-xs text-gray-600">
        <span>Live-universe figure on the executive view uses</span>
        <select
          data-testid="cloud-basis-live-select"
          value={scopes.live_basis || ''}
          disabled={busy}
          onChange={(e) => setLive(e.target.value)}
          className="text-xs border border-gray-300 rounded px-1.5 py-1 bg-white"
        >
          {scopes.bases.map((b) => <option key={b.basis_key} value={b.basis_key}>{b.basis_key}</option>)}
        </select>
        {busy && <Loader2 className="w-3 h-3 animate-spin text-amber-600" />}
      </div>

      <div>
        <p className="text-[11px] text-gray-500 mb-1">Configured bases — the three seeded defaults cannot be removed</p>
        {custom.length === 0 ? (
          <p className="text-xs text-gray-400">No added bases.</p>
        ) : (
          <div className="space-y-1">
            {custom.map((b) => (
              <div key={b.basis_key} className="flex items-center gap-2 text-xs text-gray-700">
                <span className="font-semibold">{b.basis_key}</span>
                <span className="text-gray-500 truncate">{b.predicate_text}</span>
                <button
                  data-testid={`cloud-basis-remove-${b.basis_key}`}
                  onClick={() => removeBasis(b.basis_key)}
                  disabled={busy}
                  className="ml-auto flex items-center gap-1 text-[11px] text-red-600 hover:underline disabled:opacity-50"
                >
                  <Trash2 className="w-3 h-3" /> Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
        <p className="text-[11px] font-semibold text-gray-600">Add a basis</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            data-testid="cloud-basis-key"
            value={key}
            onChange={(e) => setKey(e.target.value.toUpperCase())}
            placeholder="KEY (e.g. INSTALLED_BASE)"
            className="text-xs border border-gray-300 rounded px-2 py-1.5"
          />
          <input
            data-testid="cloud-basis-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (e.g. Installed base)"
            maxLength={80}
            className="text-xs border border-gray-300 rounded px-2 py-1.5"
          />
        </div>
        <input
          data-testid="cloud-basis-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          maxLength={300}
          className="w-full text-xs border border-gray-300 rounded px-2 py-1.5"
        />
        <p className="text-[11px] text-gray-500">An application is in the basis when every condition holds.</p>
        {conditions.map((c, i) => (
          <div key={i} className="flex items-center gap-1.5 flex-wrap">
            <select
              data-testid={`cloud-basis-field-${i}`}
              value={c.field}
              onChange={(e) => updateCondition(i, { field: e.target.value })}
              className="text-xs border border-gray-300 rounded px-1.5 py-1 bg-white"
            >
              {fields.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <select
              data-testid={`cloud-basis-op-${i}`}
              value={c.op}
              onChange={(e) => updateCondition(i, { op: e.target.value })}
              className="text-xs border border-gray-300 rounded px-1.5 py-1 bg-white"
            >
              {ops.map((o) => <option key={o} value={o}>{OP_LABEL[o] || o}</option>)}
            </select>
            {c.op !== 'is_true' && (
              <input
                data-testid={`cloud-basis-value-${i}`}
                value={c.value}
                onChange={(e) => updateCondition(i, { value: e.target.value })}
                placeholder={c.op === 'in' ? 'Approved, Partially Approved' : 'Installed'}
                className="flex-1 min-w-[8rem] text-xs border border-gray-300 rounded px-2 py-1"
              />
            )}
            {conditions.length > 1 && (
              <button
                onClick={() => setConditions((list) => list.filter((_, j) => j !== i))}
                className="text-gray-400 hover:text-red-600"
                aria-label="Remove condition"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setConditions((list) => [...list, blankCondition()])}
            className="flex items-center gap-1 text-[11px] text-amber-700 hover:underline"
          >
            <Plus className="w-3 h-3" /> Add condition
          </button>
          <button
            data-testid="cloud-basis-add"
            onClick={addBasis}
            disabled={!canSave}
            className="ml-auto text-xs px-3 py-1 rounded-lg bg-amber-500 text-white disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Add basis'}
          </button>
        </div>
      </div>
    </div>
  );
}
